import { useState } from "react";
import { Link } from "react-router-dom";
import { useTheme } from "../components/ThemeProvider";
import VisorPlantillaImpresion from "../components/plantillas/VisorPlantillaImpresion";
import usePermiso from "../hooks/usePermiso";

const PLANTILLAS = [
  {
    id: "ficha_captacion_papel",
    nodeId: "pla_imp_captacion",
    emoji: "📝",
    label: "Ficha Captación (papel)",
    formato: "DIN A4",
    orientacion: "Vertical",
    desc: "Formulario en blanco para rellenar en visita. Mismo orden que la ficha CRM (secciones 1–9). Impresión a doble cara.",
    color: "#2563eb",
    archivo: "/plantillas/ficha_captacion_papel_a4.html",
    tags: ["Captación", "Agente", "A4", "Papel"],
  },
  {
    id: "ficha_propiedad_a4",
    nodeId: "pla_imp_propiedad",
    emoji: "🏠",
    label: "Ficha Propiedad",
    formato: "DIN A4",
    orientacion: "Vertical",
    desc: "Ficha comercial de una cara para presentar una propiedad al cliente. Incluye fotos, características técnicas, descripción y datos del agente.",
    color: "#c8002a",
    archivo: "/plantillas/ficha_propiedad_a4.html",
    tags: ["Venta", "Alquiler", "A4"],
  },
  {
    id: "ficha_escaparate_a3",
    nodeId: "pla_imp_escaparate",
    emoji: "🏷️",
    label: "Ficha Escaparate",
    formato: "DIN A3",
    orientacion: "Vertical",
    desc: "Cartel de gran formato para colocar en el escaparate o en el interior del inmueble. Alta visibilidad con foto protagonista y datos clave.",
    color: "#2563eb",
    archivo: "/plantillas/ficha_escaparate_a3.html",
    tags: ["Escaparate", "A3"],
  },
  {
    id: "ficha_cita_agente_a4",
    nodeId: "pla_imp_cita",
    emoji: "📋",
    label: "Ficha Cita Agente",
    formato: "DIN A4",
    orientacion: "Vertical",
    desc: "Hoja de trabajo para el agente durante una visita. Incluye datos de la cita, del cliente, resumen de la propiedad y espacio para notas.",
    color: "#16a34a",
    archivo: "/plantillas/ficha_cita_agente_a4.html",
    tags: ["Visitas", "Agente", "A4"],
  },
  {
    id: "ficha_comparativa_a4",
    nodeId: "pla_imp_comparativa",
    emoji: "📊",
    label: "Ficha Comparativa",
    formato: "DIN A4",
    orientacion: "Horizontal",
    desc: "Tabla comparativa para presentar varias propiedades lado a lado. Ideal para reuniones con clientes que barajan múltiples opciones.",
    color: "#7c3aed",
    archivo: "/plantillas/ficha_comparativa_a4.html",
    tags: ["Comparativa", "A4"],
  },
];

export default function PlantillasImpresion() {
  const { theme } = useTheme();
  const c = theme.colors;
  const isDark = theme.name === "dark";
  const { puede } = usePermiso();
  const plantillasVisibles = PLANTILLAS.filter((p) => puede(p.nodeId, "ver"));
  const [plantillaAbierta, setPlantillaAbierta] = useState(null);

  return (
    <div className="min-h-screen" style={{ background: c.bgApp }}>
      {/* Cabecera */}
      <div
        style={{
          background: c.bgCard,
          borderBottom: `1px solid ${c.border}`,
          padding: "28px 32px 24px",
        }}
      >
        <div style={{ display: "flex", alignItems: "flex-start", gap: 16 }}>
          <div
            style={{
              width: 48,
              height: 48,
              borderRadius: 12,
              background: "linear-gradient(135deg,#c8002a,#ff2255)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: 22,
              flexShrink: 0,
            }}
          >
            🖨️
          </div>
          <div>
            <h1
              style={{
                margin: 0,
                fontSize: 22,
                fontWeight: 700,
                color: c.textPrimary,
                letterSpacing: "-0.3px",
              }}
            >
              Plantillas de impresión
            </h1>
            <p style={{ margin: "4px 0 0", fontSize: 13, color: c.textMuted, lineHeight: 1.5 }}>
              Formatos prediseñados para imprimir y presentar propiedades a clientes.
              Ábrelos en el visor del CRM, revisa la maquetación e imprime desde ahí.
            </p>
          </div>
        </div>

        {/* Nota informativa */}
        <div
          style={{
            marginTop: 18,
            padding: "10px 16px",
            borderRadius: 10,
            background: isDark ? "rgba(37,99,235,0.08)" : "#eff6ff",
            border: `1px solid ${isDark ? "rgba(37,99,235,0.2)" : "#bfdbfe"}`,
            fontSize: 12,
            color: isDark ? "#93c5fd" : "#1d4ed8",
            lineHeight: 1.6,
            display: "flex",
            gap: 10,
            alignItems: "flex-start",
          }}
        >
          <span style={{ fontSize: 15, flexShrink: 0 }}>💡</span>
          <span>
            Para imprimir con datos reales, selecciona propiedades en la tabla de{" "}
            <strong>Propiedades</strong> o en la ficha de un <strong>cliente</strong> y
            usa el botón <strong>Acciones → Imprimir</strong>. Los textos dinámicos (
            <code style={{ fontSize: 11 }}>{"{{DOCUMENTACION:slug}}"}</code>) se editan en{" "}
            <Link to="/plantillas/documentacion" style={{ fontWeight: 700, textDecoration: "underline" }}>
              Documentación
            </Link>{" "}
            y las respuestas rápidas de WhatsApp en{" "}
            <Link to="/plantillas/whatsapp" style={{ fontWeight: 700, textDecoration: "underline" }}>
              WhatsApp
            </Link>
            .
          </span>
        </div>
      </div>

      {/* Grid de plantillas */}
      <div
        style={{
          padding: "28px 32px",
          display: "grid",
          gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
          gap: 20,
          maxWidth: 1100,
        }}
      >
        {plantillasVisibles.map((p) => (
          <div
            key={p.id}
            style={{
              background: c.bgCard,
              borderRadius: 16,
              border: `1px solid ${c.border}`,
              overflow: "hidden",
              display: "flex",
              flexDirection: "column",
              transition: "box-shadow 0.2s, transform 0.2s",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.boxShadow = isDark
                ? "0 8px 32px rgba(0,0,0,0.4)"
                : "0 8px 24px rgba(0,0,0,0.1)";
              e.currentTarget.style.transform = "translateY(-2px)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.boxShadow = "none";
              e.currentTarget.style.transform = "none";
            }}
          >
            {/* Barra de color superior */}
            <div style={{ height: 4, background: p.color }} />

            {/* Cuerpo */}
            <div style={{ padding: "20px 20px 16px", flex: 1 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
                <div
                  style={{
                    width: 40,
                    height: 40,
                    borderRadius: 10,
                    background: p.color + "18",
                    border: `1.5px solid ${p.color}40`,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: 20,
                    flexShrink: 0,
                  }}
                >
                  {p.emoji}
                </div>
                <div>
                  <div style={{ fontSize: 15, fontWeight: 700, color: c.textPrimary }}>
                    {p.label}
                  </div>
                  <div style={{ display: "flex", gap: 5, marginTop: 3 }}>
                    <span
                      style={{
                        fontSize: 10,
                        fontWeight: 700,
                        letterSpacing: "0.8px",
                        textTransform: "uppercase",
                        color: p.color,
                        background: p.color + "15",
                        padding: "1px 7px",
                        borderRadius: 4,
                      }}
                    >
                      {p.formato}
                    </span>
                    <span
                      style={{
                        fontSize: 10,
                        fontWeight: 600,
                        color: c.textMuted,
                        background: isDark ? "rgba(255,255,255,0.06)" : "#f1f5f9",
                        padding: "1px 7px",
                        borderRadius: 4,
                      }}
                    >
                      {p.orientacion}
                    </span>
                  </div>
                </div>
              </div>

              <p style={{ fontSize: 12.5, color: c.textMuted, lineHeight: 1.65, margin: 0 }}>
                {p.desc}
              </p>

              <div style={{ display: "flex", flexWrap: "wrap", gap: 4, marginTop: 12 }}>
                {p.tags.map((t) => (
                  <span
                    key={t}
                    style={{
                      fontSize: 10,
                      fontWeight: 500,
                      color: c.textMuted,
                      background: isDark ? "rgba(255,255,255,0.05)" : "#f8fafc",
                      border: `1px solid ${c.border}`,
                      padding: "2px 8px",
                      borderRadius: 4,
                    }}
                  >
                    {t}
                  </span>
                ))}
              </div>
            </div>

            {/* Footer con botón */}
            <div
              style={{
                padding: "12px 20px 16px",
                borderTop: `1px solid ${c.border}`,
              }}
            >
              <button
                type="button"
                onClick={() => setPlantillaAbierta(p)}
                style={{
                  width: "100%",
                  padding: "10px 0",
                  borderRadius: 9,
                  border: "none",
                  background: p.color,
                  color: "#fff",
                  fontSize: 13,
                  fontWeight: 700,
                  cursor: "pointer",
                  fontFamily: "inherit",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 7,
                  transition: "opacity 0.15s",
                }}
                onMouseEnter={(e) => { e.currentTarget.style.opacity = "0.87"; }}
                onMouseLeave={(e) => { e.currentTarget.style.opacity = "1"; }}
              >
                <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M18 13v6a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2h6" />
                  <polyline points="15 3 21 3 21 9" />
                  <line x1="10" y1="14" x2="21" y2="3" />
                </svg>
                Ver plantilla
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Sección de uso desde propiedades */}
      <div style={{ padding: "0 32px 40px" }}>
        <div
          style={{
            background: c.bgCard,
            borderRadius: 16,
            border: `1px solid ${c.border}`,
            padding: "22px 24px",
            maxWidth: 1100,
          }}
        >
          <h2 style={{ margin: "0 0 14px", fontSize: 15, fontWeight: 700, color: c.textPrimary }}>
            ¿Cómo imprimir con datos reales?
          </h2>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 16 }}>
            {[
              {
                icon: "🏠",
                titulo: "Desde Propiedades",
                pasos: ["Abre la tabla de Propiedades", "Marca una o varias filas (checkbox)", "Clic en «Acciones»", "Selecciona «Imprimir»", "Elige el formato de plantilla"],
              },
              {
                icon: "👤",
                titulo: "Desde Ficha de Cliente",
                pasos: ["Abre la ficha de un cliente", "En el panel de propiedades encontradas", "Marca las propiedades con el checkbox", "Clic en «Imprimir» (acciones rápidas)", "Elige el formato de plantilla"],
              },
            ].map((item) => (
              <div key={item.titulo}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
                  <span style={{ fontSize: 18 }}>{item.icon}</span>
                  <span style={{ fontSize: 13, fontWeight: 700, color: c.textPrimary }}>{item.titulo}</span>
                </div>
                <ol style={{ margin: 0, paddingLeft: 18 }}>
                  {item.pasos.map((paso, i) => (
                    <li key={i} style={{ fontSize: 12, color: c.textMuted, marginBottom: 4, lineHeight: 1.5 }}>
                      {paso}
                    </li>
                  ))}
                </ol>
              </div>
            ))}
          </div>
        </div>
      </div>

      <VisorPlantillaImpresion
        plantilla={plantillaAbierta}
        onClose={() => setPlantillaAbierta(null)}
      />
    </div>
  );
}
